// src/components/superadmin/ClientList.jsx
// Search bar, column sort, status/module filters, health dot, CSV export,
// expandable drill-down row and "View Portal" quick-link.
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMemberCount, getVolunteerCount } from '../../api/superAdminApi';
import { MODULE_ICONS, MODULE_LABELS, TOGGLEABLE_MODULE_KEYS } from './moduleMeta';

const FILTERABLE_MODULES = TOGGLEABLE_MODULE_KEYS.filter(k => k !== 'bespokeai');

function ClientList({ clients, onCreateClient }) {
  const navigate = useNavigate();

  const [search,       setSearch]       = useState('');
  const [sortKey,      setSortKey]      = useState('name');
  const [sortDir,      setSortDir]      = useState('asc');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterModule, setFilterModule] = useState('');

  const [expandedId,       setExpandedId]       = useState(null);
  const [drilldownData,    setDrilldownData]    = useState({});
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  const getOrgId        = (c) => c.orgId || c.id || 'Not configured';
  const getClientStatus = (c) => c.status || 'active';

  const getHealth = (client) => {
    if (getClientStatus(client) === 'inactive') return 'red';
    if (client.updatedAt) {
      const updated   = client.updatedAt?.seconds
        ? new Date(client.updatedAt.seconds * 1000)
        : new Date(client.updatedAt);
      const daysSince = (Date.now() - updated.getTime()) / 86400000;
      if (daysSince > 30) return 'amber';
    }
    return 'green';
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const sortArrow = (key) => {
    if (sortKey !== key) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const sortedFiltered = clients
    .filter(c => {
      const name = (c.name || '').toLowerCase();
      const oid  = getOrgId(c).toLowerCase();
      const q    = search.toLowerCase();
      if (q && !name.includes(q) && !oid.includes(q)) return false;
      if (filterStatus && getClientStatus(c) !== filterStatus)  return false;
      if (filterModule && !c.enabledModules?.[filterModule])    return false;
      return true;
    })
    .sort((a, b) => {
      let av, bv;
      if (sortKey === 'name') {
        av = (a.name || '').toLowerCase();
        bv = (b.name || '').toLowerCase();
      } else if (sortKey === 'createdAt') {
        av = a.createdAt?.seconds ?? 0;
        bv = b.createdAt?.seconds ?? 0;
      } else if (sortKey === 'status') {
        av = getClientStatus(a);
        bv = getClientStatus(b);
      } else {
        av = ''; bv = '';
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });

  const getEnabledModules = (client) => [
    'core',
    ...TOGGLEABLE_MODULE_KEYS.filter(k => client.enabledModules?.[k]),
  ];

  const handleExportCSV = () => {
    const headers = ['Client Name', 'Org ID', 'Status', 'Modules', 'Created'];
    const rows    = sortedFiltered.map(c => [
      c.name || '',
      getOrgId(c),
      getClientStatus(c),
      getEnabledModules(c).map(m => MODULE_LABELS[m]).join(' | '),
      c.createdAt?.seconds
        ? new Date(c.createdAt.seconds * 1000).toLocaleDateString('en-SG')
        : '',
    ]);
    const csv  = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `bespoke-clients-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchDrilldown = useCallback(async (client) => {
    const orgId = getOrgId(client);
    if (!orgId || orgId === 'Not configured') return;
    if (drilldownData[client.id]) return; // cached

    setDrilldownLoading(true);
    const [memberCount, volunteerCount] = await Promise.all([
      getMemberCount(orgId),
      getVolunteerCount(orgId),
    ]);
    setDrilldownData(prev => ({
      ...prev,
      [client.id]: { memberCount, volunteerCount },
    }));
    setDrilldownLoading(false);
  }, [drilldownData]);

  const toggleExpand = (client) => {
    if (expandedId === client.id) {
      setExpandedId(null);
    } else {
      setExpandedId(client.id);
      fetchDrilldown(client);
    }
  };

  const handleViewAsClient = (e, client) => {
    e.stopPropagation();
    const orgId = getOrgId(client);
    if (orgId && orgId !== 'Not configured') {
      window.open(`/BespokeBridge/${orgId}/admin/login`, '_blank');
    }
  };

  const handleConfigureClient = (e, clientId) => {
    e.stopPropagation();
    navigate(`/superadmin/client-config/${clientId}`);
  };

  return (
    <div className="client-list-table-container">

      <div className="client-list-actions-bar">
        {onCreateClient && (
          <button className="create-client-button" onClick={onCreateClient}>
            + Create New Client
          </button>
        )}
        <button className="sa-export-btn" onClick={handleExportCSV}>
          ↓ Export CSV
        </button>
      </div>

      <div className="sa-client-filter-bar">
        <input
          className="sa-client-search"
          type="text"
          placeholder="Search by name or org ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="sa-client-filter-select"
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
        </select>
        <select
          className="sa-client-filter-select"
          value={filterModule}
          onChange={e => setFilterModule(e.target.value)}
        >
          <option value="">All Modules</option>
          {FILTERABLE_MODULES.map(k => (
            <option key={k} value={k}>{MODULE_LABELS[k]}</option>
          ))}
          <option value="bespokeai">BespokeAI</option>
        </select>
        <span className="sa-client-count">
          {sortedFiltered.length} of {clients.length} clients
        </span>
      </div>

      <table className="client-list-table">
        <thead>
          <tr>
            <th style={{ width: 28 }} />
            <th className="sortable" onClick={() => handleSort('name')}>
              Client Name{sortArrow('name')}
            </th>
            <th>Org ID</th>
            <th className="sortable" onClick={() => handleSort('status')}>
              Status{sortArrow('status')}
            </th>
            <th>Modules</th>
            <th className="sortable" onClick={() => handleSort('createdAt')}>
              Created{sortArrow('createdAt')}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedFiltered.map(client => {
            const isExpanded = expandedId === client.id;
            const health     = getHealth(client);
            const status     = getClientStatus(client);
            const dd         = drilldownData[client.id];
            const enabledModules = getEnabledModules(client);

            return (
              <React.Fragment key={client.id}>
                <tr
                  className={`client-row ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleExpand(client)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <span
                      className={`sa-health-dot sa-health-dot--${health}`}
                      title={
                        health === 'green' ? 'Active — recent activity' :
                        health === 'amber' ? 'Active — no recent activity' :
                                             'Inactive'
                      }
                    />
                  </td>
                  <td className="client-name-cell">
                    <strong>{client.name}</strong>
                  </td>
                  <td className="company-id-cell">{getOrgId(client)}</td>
                  <td className="status-cell">
                    <span className={`status-badge-table ${status}`}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  </td>
                  <td className="modules-cell">
                    <div className="sa-module-chips">
                      {enabledModules.map(m => (
                        <span key={m} className="sa-module-chip" title={MODULE_LABELS[m]}>
                          <img src={MODULE_ICONS[m]} alt={MODULE_LABELS[m]} className="sa-module-chip-icon" />
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    {client.createdAt?.seconds
                      ? new Date(client.createdAt.seconds * 1000).toLocaleDateString('en-SG', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })
                      : '—'}
                  </td>
                  <td className="actions-cell" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={(e) => handleConfigureClient(e, client.id)}
                      className="configure-button"
                    >
                      Configure
                    </button>
                    <button
                      onClick={(e) => handleViewAsClient(e, client)}
                      className="sa-view-client-btn"
                      title="Open this organisation's admin portal in a new tab"
                    >
                      View Portal ↗
                    </button>
                  </td>
                </tr>

                {isExpanded && (
                  <tr className="sa-drilldown-row">
                    <td colSpan={7}>
                      <div className="sa-drilldown-panel">
                        {drilldownLoading && !dd && (
                          <span className="sa-drilldown-loading">Loading details…</span>
                        )}

                        {dd && (
                          <div className="sa-drilldown-stats">
                            <div className="sa-drilldown-stat">
                              <span className="sa-drilldown-stat-value">{dd.memberCount}</span>
                              <span className="sa-drilldown-stat-key">Members</span>
                            </div>
                            <div className="sa-drilldown-stat">
                              <span className="sa-drilldown-stat-value">{dd.volunteerCount}</span>
                              <span className="sa-drilldown-stat-key">Volunteers</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {sortedFiltered.length === 0 && (
        <div className="empty-state">
          <p>
            {clients.length === 0
              ? 'No clients found. Create your first client to get started.'
              : 'No clients match the current search or filters.'}
          </p>
        </div>
      )}
    </div>
  );
}

export default ClientList;
